import KnxBridge from "../KnxBridge";
import Actor from "../models/Actor";
import BaseModelMap from "../models/BaseModelMap";
import Button from "../models/Button";
import Cabinet from "../models/Cabinet";
import { ChannelType } from "../models/ChannelType";
import Device from "../models/Device";
import DeviceTemplate from "../models/DeviceTemplate";
import Floor from "../models/Floor";
import { getDecodedMetadata } from "../models/KnxprodLoader";
import Page from "../models/Page";
import Room from "../models/Room";
import { getTemplateGroup } from "../models/TemplateGroup";
import ProductStorageHandler from "../persistence/ProductStorageHandle";
import { itx } from "../translationItx/itx";
import { knxReadBack } from "./KnxReadback";
import ReadBackViaKnxBridge from "./ReadBackViaKnxBridge";

const deviceImportMode = {
  createDevice: "0",
  fullReadback: "1",
};

const rb = knxReadBack();

class ReadBackClass {
  ////////////////////////////////////
  /// Readback device helper functions
  ////////////////////////////////////

  // Create Device and it's parent structure in Navigation Tree (room / floor / cabinet etc.)

  // Start function (Req Parameter: Device Physical Address)
  async readBackDeviceUsingAddress(deviceAddress: string, fullImport: boolean): Promise<boolean> {
    let toastmsg = "";
    let res = false;

    const deviceNotFoundMsg = `${itx("knxBridge.readBackDeviceNotFound", `Device not found in network with address: `)}${deviceAddress}`;
    try {
      let deviceInNetwork = "";
      try {
        rb.setToastText(rb.toastTitle, itx("knxBridge.checkingDeviceAvailibility", "Checking device availibility... "));
        deviceInNetwork = await KnxBridge.isDevicePresentInNetwork(deviceAddress);
      } catch (error: any) {
        if (error.toString().includes("deviceNotReachable")) {
          toastmsg = deviceNotFoundMsg;
        }
      }

      if (deviceInNetwork == "success") {
        rb.setToastText(rb.toastTitle, itx("knxBridge.readBackInProgress", `Device creation in progress... `));
        const manId = await KnxBridge.getManIdFromDevice(deviceAddress); // PID 12
        let appVersion = await KnxBridge.getDeviceProperty(deviceAddress, "13", "3"); // ProductProc ObjType 4, PID 25
        if (!appVersion) appVersion = await KnxBridge.getDeviceProperty(deviceAddress, "13", "4"); // MergedProc ObjType 4, PID 25

        if (appVersion && appVersion.length == 10) {
          const metadata = await this.identifyProductXMLFromDeviceProps(appVersion);
          if (metadata) {
            // Create Get the product Application xml based on above data
            res = await this.createAndAssignToNewDevice(metadata, deviceAddress, fullImport);
            // Readback success
            // toastmsg = `${itx("knxBridge.readBackSuccess", `Creation successful for `)}${deviceAddress}`;
          } else {
            // Metadata not found for device
            toastmsg = `${itx("knxBridge.readBackMetadataNotFound", `No Metadata found for device `)}${deviceAddress}`;
          }
        } else {
          // DeviceAddress not available in network
          toastmsg = deviceNotFoundMsg;
        }
      } else {
        toastmsg = deviceNotFoundMsg;
      }
    } catch (ex) {
      // ReadbackFailed
      toastmsg = `${itx("knxBridge.readBackFailed", `Failed to readback device: `)}`;
      console.error(ex);
    }
    if (toastmsg) {
      rb.setToastText(rb.toastTitle, toastmsg);
    }
    return res;
  }

  // Get Metadata
  async identifyProductXMLFromDeviceProps(appVersion: string) {
    const manufacturer = appVersion.substring(0, 4);
    const appNumber = appVersion.substring(4, 8);
    const version = appVersion.substring(8, 10);

    const azureCatalogData = await ProductStorageHandler.getAllMetadata("import");

    let metadata: any = {};
    let CatalogId: string = "";
    let appVersionDiff = 100000000; // Starting high value
    azureCatalogData.forEach((data) => {
      data = getDecodedMetadata(data);

      let manIdFromXml = data.ManufacturerId;
      let appNumberFromXml = data.ApplicationNumber;
      let appVersionFromXml = data.ApplicationVersion;

      if (
        manIdFromXml == `M-${manufacturer}` &&
        parseInt(appNumberFromXml) == parseInt(appNumber, 16) &&
        Math.abs(parseInt(appVersionFromXml) - parseInt(version, 16)) < appVersionDiff
        // App Version check here considers the application version closer to the one in memory propvided both manufacturer and ApplicationNumber are same
        // (Eg. MDT BE-GT2TX.02 in our Catalog has version 48 but device has version 49. so pick this version as device firmware and thereby memorymap will also be compatible).
      ) {
        metadata = data;
        CatalogId = data.CatalogId;
        appVersionDiff = Math.abs(parseInt(appVersionFromXml) - parseInt(version, 16));
      }
    });

    if (CatalogId) {
      rb.setToastText(rb.toastTitle, itx("knxBridge.foundDeviceInCatalog", "Found Device In Azure Catalog Store."));
      return metadata;
    }
    return undefined;
  }

  // Sequence of operations for Tree Hierarchy creation (Floor/room/cabinet/device)
  async createAndAssignToNewDevice(metadata: any, deviceAddress: string, fullImport: boolean): Promise<boolean> {
    const productType = metadata?.ProductType || "";
    let res = false;
    // Create New Cabinet with name @import
    const cabinet = this.createNewCabinet(state.cabinets, "@I", "imported");

    if (productType == "Actor") {
      const actor = await this.createNewActor(cabinet, metadata, deviceAddress);
      if (actor) {
        if (fullImport) {
          await this.readbackDevice(actor);
          console.log("Actor readback complete: ", actor);
        } else {
          console.log("Actor creation complete: ", actor);
        }
        // if (actor.templateId) state.activePanes.closePane(actor.templateId);
        res = true;
      } else {
        rb.setToastText(rb.toastTitle, itx("deviceProgramming.buttonNotCreated", `Error: Actor Not Created!!!`));
      }
    } else if (productType == "Button" || productType == "ButtonAktor") {
      // Create New Floor with name @ import
      const floor = this.createNewFloor(state.floors, "@I", "imported");

      // Create New Room with name @ import
      const room: Room = await this.createNewRoom(floor, "@I", "imported");

      // Create new button Device with name devcieAddress
      const button = await this.createNewButton(room, metadata, deviceAddress);

      // Readback All 4 Tables from Actual physical button and assign it to newly created button in Estada
      if (button) {
        if (fullImport) {
          await this.readbackDevice(button);
          console.log("Button readback complete: ", button);
        } else {
          console.log("Button creation complete: ", button);
        }
        // if (button.templateId) state.activePanes.closePane(button.templateId);
        res = true;
      } else {
        rb.setToastText(rb.toastTitle, itx("deviceProgramming.buttonNotCreated", `Error: Button Not Created!!!`));
      }
      // if (floor.id) state.activePanes.closePane(floor.id);
      // if (room.id) state.activePanes.closePane(room.id);
    } else {
      rb.setToastText(
        rb.toastTitle,
        `${itx("deviceProgramming.productTypeInvalid", `Device is neither Actor Nor Button. Detected device type: `)} ${productType}`,
      );
    }

    return res;
  }

  // Main readback function
  async readbackDevice(device: Button | Actor) {
    await ReadBackViaKnxBridge.readbackDeviceMemory(device as Device);
  }

  ////////////////////////////////////////////////
  // Helper Functions for above
  ////////////////////////////////////////////////

  createNewCabinet(parent: BaseModelMap<Cabinet>, cabinetTag: string, cabinetName: string) {
    //check if tag is double

    const listAllCabinetsTags = new Array<string>();
    state.cabinets.forEach((cabinet) => {
      listAllCabinetsTags.push(cabinet.tag);
    });
    let tagFromInput = cabinetTag;
    let nameFromInput = cabinetName;

    const newCabinet: Cabinet = new Cabinet({ name: nameFromInput, tag: tagFromInput }, parent);
    if (!listAllCabinetsTags.includes(cabinetTag)) {
      newCabinet.tag = state.nameAndTagManager.generateTag(newCabinet);
      parent?.add(newCabinet);
      const floorName = "Floor @import";
      const floor = state.floors.findFirst((f) => f.name == floorName);
      if (floor) {
        floor.cabinetId = newCabinet.id;
        floor.raiseOnChange("cabinetId");

        const roomName = "Room @import";
        const room = floor.rooms.findFirst((f) => f.name == roomName);
        if (room) {
          room.cabinetId = newCabinet.id;
          room.raiseOnChange("cabinetId");
        }
      }
      state.selectionManager.selectedObject = newCabinet;
      return newCabinet;
    } else {
      //setTag(getNewCabinTag()); //doesn`t get new Render
      rb.setToastText(
        rb.toastTitle,
        itx(
          "cabinetDialog.tagAlreadyInOtherCabinet",
          `Ihr eingegebener Tag ist schon an deinem anderen Cabinet vergeben bitte ändern Sie diesen`,
        ),
      );

      let foundcabinet = state.cabinets.findFirst((f) => f.name == cabinetName);
      if (foundcabinet) {
        return foundcabinet;
      }
    }
    return newCabinet;
  }

  createNewFloor(parent: BaseModelMap<Floor>, floorTag: string, floorName: string) {
    //check if tag is double
    const listAllFloorsTags = new Array<string>();
    state.floors.forEach((floor) => {
      listAllFloorsTags.push(floor.tag);
    });

    let xcabinetId = "";
    let cabinetName = "imported";
    let cabinet = state.cabinets.findFirst((f) => f.name == cabinetName);
    if (cabinet) {
      xcabinetId = cabinet.id;
    } else {
      const newCabinet = new Cabinet(
        {
          name: cabinetName,
        },
        state.cabinets,
      );
      newCabinet.tag = "@I"; // no itx
      state.cabinets.add(newCabinet);
      xcabinetId = newCabinet.id;
    }

    let foundFloor = parent.findFirst((f) => f.name == floorName);
    if (foundFloor) {
      foundFloor.treeViewModel.handleOpen();
      return foundFloor;
    }

    const newFloor = new Floor({ name: floorName, tag: floorTag, cabinetId: xcabinetId }, parent);
    parent?.add(newFloor);
    state.selectionManager.selectedObject = newFloor;
    // now open Floor
    newFloor.treeViewModel.handleOpen();
    return newFloor;
  }

  async createNewRoom(floor: Floor, roomTag: string, roomName: string): Promise<Room> {
    let tagFromInput = roomTag;
    let nameFromInput = roomName;

    //check if tag is double
    const listAllRoomsTagInFloor = new Array<string>();
    floor.rooms.forEach((room) => {
      listAllRoomsTagInFloor.push(room.tag);
    });

    let xcabinetId = "";
    let cabinetName = "imported";
    let cabinet = state.cabinets.findFirst((f) => f.name == cabinetName);
    if (cabinet) {
      xcabinetId = cabinet.id;
    } else {
      const newCabinet = new Cabinet(
        {
          name: cabinetName,
        },
        state.cabinets,
      );
      newCabinet.tag = "@I"; // no itx
      state.cabinets.add(newCabinet);
      xcabinetId = newCabinet.id;
    }

    let foundRoom = floor.rooms.findFirst((f) => f.name == roomName);
    if (foundRoom) {
      this.handleOpen(foundRoom);
      return foundRoom;
    }

    // await addRoom(floor, nameFromInput);
    // if(!listAllRoomsTagInFloor.includes(tagFromInput)){
    // }
    const newRoom = new Room({ name: nameFromInput, tag: tagFromInput, cabinetId: xcabinetId }, floor);
    // let newRoom = floor.rooms.findFirst((f) => f.name == roomName);
    if (!newRoom.page) {
      newRoom.page = new Page({}, newRoom);
    }
    if (newRoom.page) {
      const emptyBackgroundImage =
        "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAQgAAAEGCAYAAACZ/AuPAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAMTSURBVHhe7dQxAYBAEMCwA/9azwIsbE8dJEsd9NrdZwB+3F8BDgYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggDDzAjBpBdXtsdrzAAAAAElFTkSuQmCC"; // no itx
      this.onChangeBackgroundImgUrl(emptyBackgroundImage, newRoom.page);
    }
    floor.rooms.add(newRoom);

    this.handleOpen(newRoom);
    return newRoom;
  }

  async createNewButton(room: Room, metadata: any, deviceAddress: string) {
    const page = room.page;
    let button: Button | undefined;
    if (page) {
      var pPX = page.findFreePositionForNewObject();
      button = page.createNewButtonInPage(room, {}, pPX);
      if (button) {
        state.selectionManager.selectedObject = button;
        button.setFullPhysicalAddress(deviceAddress);
        // Button name is Combination of DeviceAddress and OrderNumber
        button.name = `${deviceAddress} - ${metadata.OrderNumber}`;
        await this.loadProductInfo(metadata, button);
      }
    }
    return button;
  }

  async createNewActor(cabinet: Cabinet, metadata: any, deviceAddress: string) {
    let newActor: Actor | undefined;

    newActor = cabinet.createActor({ channelType: ChannelType.Generic }, cabinet);
    newActor.name = `${deviceAddress} - ${metadata.OrderNumber}`;
    newActor.setFullPhysicalAddress(deviceAddress);
    //const newChannel = newActor.createNewChannel();
    newActor.numberChannelsFromUser = 0;

    await this.loadProductInfo(metadata, newActor);

    cabinet.actors.add(newActor);

    state.activePanes.openPane(newActor);
    // }
    return newActor;
  }

  async loadProductInfo(metadata: any, device: Button | Actor) {
    const productInfo = metadata;
    const catalogId = productInfo?.CatalogId;
    var groupName = device.isButton
      ? itx("components.Button", "Button")
      : itx("components.Actor", "Aktor") + " / " + itx("ServerTemplateList.CabinetElement", "Schaltschrankelement");
    let parent = getTemplateGroup(groupName);
    if (parent) {
      let importedTemplateList = parent.templates.items;

      for (const [key, value] of importedTemplateList.entries()) {
        if (value.knxProductData.modelDataId == catalogId) {
          // set template if device already imported
          if (device) {
            device.setAndLoadTemplate(value.id);
          }
          rb.setToastText(
            rb.toastTitle,
            itx("ServerTemplateList.T1AlreadyImported", "Device(s) already imported!", {
              T1: groupName,
            }),
          );
          return;
        }
      }
      let template = await DeviceTemplate.createFromServerData(parent, catalogId, productInfo);

      rb.setToastText(
        rb.toastTitle,
        itx("ServerTemplateList.yourT1hasBeenImportet", `Ihr ausgewählter ${groupName} wurde herruntergeladen`, {
          T1: groupName,
        }),
      );
      state.selectionManager.selectedObject = template;
      if (device) {
        device.setAndLoadTemplate(template.id);
      }
    }
  }

  onChangeBackgroundImgUrl(backgroundImgUrl: string, page: Page) {
    async function getImagevirtualElement(url: string) {
      const img = new Image();
      img.src = url;
      await img.decode();
      return img;
    }
    const mainDrawingArea = document.getElementById("mainDrawingArea");
    let imageHeight = 0;
    let imageWidth = 0;

    let areaWidth = 21 * 33;
    let areaHeight = 29.7 * 33;
    if (mainDrawingArea) {
      areaWidth = mainDrawingArea.offsetHeight;
      areaHeight = mainDrawingArea.offsetWidth;
    }
    page.resizeImageAfterLoad(backgroundImgUrl).then((result) => {
      page.backgroundImgUrl = result as string;
      getImagevirtualElement(result as string).then((img) => {
        imageWidth = img.naturalWidth;
        imageHeight = img.naturalHeight;
        page.backgroundImagePosition.posX = (areaHeight - imageHeight) / 2;
        page.backgroundImagePosition.posY = (areaWidth - imageWidth) / 2;
        page.raiseOnChange("_backgroundImgUrl");
      });
    });
  }

  handleOpen(newRoom: Room) {
    if (newRoom.page == undefined) {
      newRoom.page = new Page({}, newRoom);
    }

    const pageId = newRoom.page.id;
    if (state.activePanes.isOpen(pageId)) {
      state.activePanes.activatePane(pageId);
    } else {
      state.activePanes.loadPage(pageId);
    }
  }
}

const ReadBack = new ReadBackClass();
export default ReadBack;
